import Parse from "parse";
import * as ReactSentry from "@sentry/react";
import * as cloud from "../ajax/cloud";
import { getXmppLogin } from "./selectors";

import {
  signOutSuccess,
  logInSuccess,
  updateUserSuccess,
  synchronizeUserSuccess,
  signOutRequest
} from "./actions";
import { appFailure } from "src/duck/actions";
import { CACHE_NAME } from "src/modules/activity-log/constants";
import {
  updateMotionCapture,
  updateNoiseCapture
} from "src/modules/activity-log/duck/actions";
import { getActivityLogUploader } from "src/modules/activity-log/lib/ActivityLogUploader";
import environment from "src/utils/environment";

const { isBrowser } = environment;
const Sentry = isBrowser ? ReactSentry : window.require("@sentry/electron");
const log = window.require("electron-log");

async function clearData() {
  await getActivityLogUploader().clearData();
}
async function clearCache() {
  await caches.delete(CACHE_NAME);
}

export function signUp({ familyName, email, password, app }) {
  return async (dispatch, getState) => {
    const user = await cloud.signUp({ familyName, email, password });
    Sentry?.configureScope((scope) => {
      scope.setUser({ id: user.objectId, email });
    });
    dispatch(synchronizeUserSuccess(user));
    app.synchronizer.announceItemChange(
      app.synchronizer.SynchronizableItems.CLOUD_ACCOUNT
    );
  };
}

export function relogAsDifferentUser({ authId, otp, messenger, app }) {
  return async (dispatch, getState) => {
    const xmppLogin = getXmppLogin(getState());

    try {
      const newUser = await cloud.logInWithOtp({ authId, otp, xmppLogin });
      Sentry?.configureScope((scope) => {
        scope.setUser({
          id: newUser.objectId,
          email: newUser.email,
          optionalEmail: newUser.optionalEmail
        });
      });
      await clearCache();
      await clearData();
      dispatch(logInSuccess(newUser));
      await app.synchronizer.synchronize({ syncAll: true });
    } catch (error) {
      log.error(error);
      dispatch(appFailure());
    }
  };
}

export function updateUser({ app, ...update }) {
  return async (dispatch) => {
    const updatedUser = await cloud.updateUser(update);
    dispatch(updateUserSuccess(updatedUser));
    app.synchronizer.announceItemChange(
      app.synchronizer.SynchronizableItems.CLOUD_ACCOUNT
    );
  };
}

export function synchronizeCloudAccount() {
  return async (dispatch) => {
    const user = await cloud.fetchCurrentUser();
    dispatch(synchronizeUserSuccess(user));
  };
}

export function synchronizeCloudSettings() {
  return async (dispatch) => {
    const params = await cloud.getCloudSettings();
    if (!params || Object.keys(params).length === 0) return;
    const { activity_log_noise_capture, activity_log_motion_capture } = params;
    if (activity_log_noise_capture) {
      dispatch(updateNoiseCapture(activity_log_noise_capture));
    }
    if (activity_log_motion_capture) {
      dispatch(updateMotionCapture(activity_log_motion_capture));
    }
  };
}

export function signOut({ app }) {
  return async (dispatch, getState) => {
    try {
      const xmppLogin = getXmppLogin(getState());
      dispatch(signOutRequest());
      const anonymousUser = await cloud.signOut(xmppLogin);
      Sentry?.configureScope((scope) => {
        scope.setUser({ id: anonymousUser.objectId });
      });
      await clearCache();
      await clearData();

      dispatch(signOutSuccess(anonymousUser));
      await app.synchronizer.synchronizeLullabies();
      app.restart();
    } catch (error) {
      log.error(error);
      dispatch(appFailure());
    }
  };
}

export function logIn({ username, password, messenger, app }) {
  return async (dispatch, getState) => {
    try {
      const xmppLogin = getXmppLogin(getState());
      const user = await cloud.logIn({ xmppLogin, username, password });
      Sentry?.configureScope((scope) => {
        scope.setUser({ id: user.objectId, email: user.email });
      });
      dispatch(logInSuccess(user));
      app.synchronizer.announceChange();
      await clearCache();
      await clearData();
      await app.synchronizer.synchronize({ syncAll: true });
    } catch (error) {
      if (
        error.code === Parse.Error.OBJECT_NOT_FOUND ||
        error.message.includes("your account has been deactivated")
      ) {
        throw error;
      }
      log.error(error);
      dispatch(appFailure());
    }
  };
}
